import React, { useContext, useState } from 'react';
import { UserDetailContext, UserDetailContextProps } from '../../../../context/userDetail/userDetailContext';
import { ButtonDownload, ButtonPrimary, ButtonTerinary, Icon } from '../../../../common/components';
import {
  DateMonthDayFilterModal,
  Ranges,
  SelectDateResult,
} from '../../../../common/components/DateMonthDayFilterModal';
import PortalFilter from '../../../../common/components/PortalFilter/PortalFilter';
import { ViewMode } from './Charts';
import { MpanFilterItem } from './types/ChartTypes';
import { canDisplayHHData } from '../../services/mpanService';
import { iconsToUse } from '../../../../common/styles/themes';
import * as variables from '../../../../common/styles/variables';
import { DownloadButtonsWrapper } from '../../../mpans/MpanDetails.styles';

export type PeriodValue = '24' | '12' | '6';

interface SidebarMobileProps {
  period: PeriodValue;
  onPeriodChange: (p: PeriodValue) => void;
  mpans: MpanFilterItem[];
  selectedMpans: MpanFilterItem[];
  onAddMpan: (mpan: MpanFilterItem) => void;
  onRemoveMpan: (m: string) => void;
  onReset?: () => void;
  selectedCustomer: string;
  onCustomerChange: (c: string) => void;
  onDateChange: (date: SelectDateResult, type: 'Monthly' | 'Daily') => void;
  view: ViewMode;
  mpansContainHH: boolean;
  downloadReport: any;
  selectedViewByIndex: any;
  viewByOptions: Array<Common.IOption>;
}

const SidebarMobile: React.FC<SidebarMobileProps> = ({
  period,
  onPeriodChange,
  mpans,
  selectedMpans,
  onAddMpan,
  onRemoveMpan,
  onReset,
  selectedCustomer,
  onCustomerChange,
  onDateChange,
  view,
  mpansContainHH,
  downloadReport,
  selectedViewByIndex,
  viewByOptions,
}) => {
  const MAX_NUMBER_OF_MPANS = 10;
  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const [periodDisabled, setPeriodDisabled] = useState<boolean>(!selectedCustomer);
  const mpanEnabled = !!selectedCustomer && !!period;
  const [showMonthlyModal, setShowMonthlyModal] = useState<boolean>(false);
  const [showDailyModal, setShowDailyModal] = useState<boolean>(false);
  const [previouslySelectedDate, setPreviouslySelectedDatel] = useState<any>();
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState<number>(-1);

  React.useEffect(() => {
    setPeriodDisabled(!selectedCustomer);

    const accountIndex = userDetail.customerAccounts.findIndex(
      customerAccount => customerAccount.essAccountID === selectedCustomer
    );
    setSelectedCustomerIndex(accountIndex);
  }, [selectedCustomer, selectedMpans]);

  const handleDateSelectorDayMonthFilterChange = (
    date: SelectDateResult,
    option: any,
    isDaily: boolean,
    isMonthly: boolean
  ) => {
    if (isMonthly) {
      onDateChange(date, 'Monthly');
      setShowMonthlyModal(false);
    } else if (isDaily) {
      onDateChange(date, 'Daily');
      setShowDailyModal(false);
    }
    setPreviouslySelectedDatel(date);
  };

  const customerAccountOptions = userDetail.customerAccounts.map((account: Api.IEssAccountData) => {
    return {
      value: account.essAccountID,
      display: account.essAccountID + ' - ' + account.accountName,
    };
  });

  const selectedMpansOptions: Common.IOption[] = mpans
    .filter(
      (mpanFilterItem: MpanFilterItem) =>
        !selectedMpans.some((mpan: MpanFilterItem) => mpan.mpan === mpanFilterItem.mpan)
    )
    .map((mpanFilterItem: MpanFilterItem): Common.IOption => {
      return {
        value: mpanFilterItem.mpan,
        display: mpanFilterItem.mpan,
      } as Common.IOption;
    });

  const handleCustomerFilterChange = (accountOption: Common.IOption, viewByIndex?: number) => {
    if (accountOption) {
      const accountIndex = userDetail.customerAccounts.findIndex(
        customerAccount => customerAccount.essAccountID === accountOption.value
      );
      setSelectedCustomerIndex(accountIndex);
      const account = userDetail.customerAccounts[accountIndex];

      if (account) onCustomerChange(account.essAccountID);

      const period = viewByOptions[viewByIndex ?? selectedViewByIndex].value;
      onPeriodChange(period as PeriodValue);
    }
  };

  const handleViewByChange = (viewByOption: Common.IOption) => {
    if (viewByOption) {
      onPeriodChange(viewByOption.value as PeriodValue);
    }
  };

  const handleMpanChange = (mpanOption: Common.IOption) => {
    if (mpanEnabled && mpanOption) {
      const mpanSelected = selectedMpans.some(
        (mpanFilterItem: MpanFilterItem) => mpanFilterItem.mpan === mpanOption.value
      );
      const maxMpanSelected = selectedMpans.length >= MAX_NUMBER_OF_MPANS;

      if (!mpanSelected && !maxMpanSelected) {
        const mpan = mpans.find(mpanDetail => mpanDetail.mpan === mpanOption.value);
        if (mpan) onAddMpan(mpan);
      }
    }
  };

  const viewByIsDisabled = periodDisabled || view === 'daily' || view === 'halfhourly';
  const mpanIsDisabled = !mpanEnabled || selectedMpans.length >= MAX_NUMBER_OF_MPANS;

  const filterMessageTitle: Array<Messages.IFilterMessageType> = [
    { value: 'xlsx', display: 'Download Excel' },
    { value: 'xml', display: 'Download XML' },
  ];

  return (
    <aside className='h-100'>
      <div className='mb-1'>
        <div className='pb-2 filter-title'>Customer Account</div>
        <PortalFilter
          className=''
          placeholder={
            selectedCustomerIndex >= 0 ? customerAccountOptions[selectedCustomerIndex].display : 'Account ref'
          }
          options={customerAccountOptions}
          selectedIndex={selectedCustomerIndex}
          isClearable={false}
          onChange={handleCustomerFilterChange}
          isIndexSet={false}
        />
      </div>

      <div className='sidebar-section mb-1'>
        <div className='pb-2 filter-title'>View by</div>
        <PortalFilter
          className={`${viewByIsDisabled && 'disabled'}`}
          options={viewByOptions}
          selectedIndex={selectedViewByIndex}
          isClearable={false}
          onChange={handleViewByChange}
          isIndexSet={false}
          isDisabled={viewByIsDisabled}
        />
      </div>

      <div className='sidebar-section mb-3'>
        <div className='pb-2 filter-title'>MPAN</div>

        <div className='mb-1'>
          <PortalFilter
            className={`${mpanIsDisabled && 'disabled'}`}
            options={selectedMpansOptions}
            placeholder={'MPAN'}
            onChange={handleMpanChange}
            isClearable={false}
            isDisabled={mpanIsDisabled}
            isIndexSet={false}
          />
        </div>
        {selectedMpans.length > 0 && (
          <div>
            {selectedMpans.map((mpanFilterItem: MpanFilterItem, idx) => (
              <div>
                <div
                  key={mpanFilterItem.mpan}
                  className='p-1 mb-1 border d-flex justify-content-between align-items-center'
                  style={{ width: '100%' }}
                >
                  <span className='pl-2'>{mpanFilterItem.mpan}</span>
                  <span>
                    {!canDisplayHHData(mpanFilterItem) && (
                      <Icon icon={iconsToUse.warning} size={variables.iconSizeSmall} />
                    )}
                  </span>
                  <button
                    className='btn btn-lg p-0 font-weight-bold'
                    title='Remove'
                    onClick={() => onRemoveMpan(mpanFilterItem.mpan)}
                  >
                    &times;
                  </button>
                </div>
              </div>
            ))}
            <div className='text-muted small mt-1'>{`${selectedMpans.length} of ${MAX_NUMBER_OF_MPANS} MPANs selected`}</div>
          </div>
        )}
      </div>

      {mpansContainHH && (
        <div className='sidebar-section'>
          <div className='filter-title'>View granularity</div>
          <div className='row'>
            <div className='col-6 d-flex justify-content-center'>
              <ButtonTerinary
                className='monthdaybtn'
                title='Month'
                icon='menuScheduledReporting'
                onClick={(e: any) => {
                  e.preventDefault();
                  setShowMonthlyModal(true);
                }}
              />
            </div>
            <div className='col-6 d-flex justify-content-center'>
              <ButtonTerinary
                className='monthdaybtn'
                title='Day'
                icon='menuScheduledReporting'
                onClick={(e: any) => {
                  e.preventDefault();
                  setShowDailyModal(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className='sidebar-section d-flex justify-content-center'>
        <ButtonPrimary
          className='mt-0'
          title='Clear'
          onClick={(e: any) => {
            e.preventDefault();
            setPeriodDisabled(true);
            setSelectedCustomerIndex(-1);
            if (onReset) {
              onReset();

              if (customerAccountOptions.length === 1) {
                const accountOption = customerAccountOptions[0];
                handleCustomerFilterChange(accountOption, 1);
              } else {
                setSelectedCustomerIndex(-1);
              }
            }
          }}
        />
      </div>

      {showMonthlyModal && (
        <DateMonthDayFilterModal
          enableDaily={false}
          onHide={() => {
            setShowMonthlyModal(false);
          }}
          enableMonthly={true}
          selectedRange={Ranges['monthly']}
          show={showMonthlyModal}
          onDateSelect={handleDateSelectorDayMonthFilterChange}
          previouslySelectedDate={previouslySelectedDate}
        />
      )}
      {showDailyModal && (
        <DateMonthDayFilterModal
          enableDaily={true}
          onHide={() => {
            setShowDailyModal(false);
          }}
          enableMonthly={false}
          selectedRange={Ranges['daily']}
          show={showDailyModal}
          onDateSelect={handleDateSelectorDayMonthFilterChange}
          previouslySelectedDate={previouslySelectedDate}
        />
      )}

      <DownloadButtonsWrapper className='row' key={'downloadbtn'}>
        <ButtonDownload
          className='ml-auto mr-auto'
          disabled={mpanIsDisabled}
          title={'Download graph data'}
          options={filterMessageTitle}
          onClick={downloadReport}
          outline={true}
          icon='upload'
          dontSaveSelectedIndex
        />
      </DownloadButtonsWrapper>
    </aside>
  );
};

export default SidebarMobile;
